'use client'

// ShadowPolicy — small things do not cast shadows.
//
// The sun's shadow camera covers the whole town and redraws every frame, so
// every caster is a draw in the shadow pass whether or not anyone can see
// it, and the town had 418 of them. About 180 are under 0.6 m across (food
// on the counters, trinkets, cans, the fruit in the beds, bolts on the
// buildings), and their shadows are a few pixels of a 2048 map stretched
// over 300 m: invisible, and still a draw each. Measured 28 Oct
// (docs/world-performance.md): ~120 fewer shadow draws.
//
// One rule, in one place, rather than a castShadow argument in every file
// that traverses a model: anything whose bounding sphere in the world is
// smaller than MIN_RADIUS stops casting. Each mesh is looked at once, when
// it first appears (a sweep a second, like the Precompiler's), so things
// that arrive later (a serving, an offering, a bought thing) follow it too.
// Skinned and instanced meshes are left alone (the bird; trees and lamps,
// whose one bounding sphere is not the size of one of them). Anything that
// should keep its shadow whatever its size says so: userData.keepShadow.

import { useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three/webgpu'

const MIN_RADIUS = 0.6
const EVERY_MS = 1000

const _scale = new THREE.Vector3()

export function ShadowPolicy() {
  const scene = useThree((s) => s.scene)

  useEffect(() => {
    const looked = new WeakSet()
    const sweep = () => {
      scene.traverse((o) => {
        // looked at once it casts: models switch castShadow on in their own
        // effects, sometimes after this has already passed them
        if (!o.isMesh || !o.castShadow || looked.has(o)) return
        looked.add(o)
        if (o.isSkinnedMesh || o.isInstancedMesh || o.userData?.keepShadow) return
        const g = o.geometry
        if (!g) return
        if (!g.boundingSphere) g.computeBoundingSphere()
        o.getWorldScale(_scale)
        const r = g.boundingSphere.radius * Math.max(Math.abs(_scale.x), Math.abs(_scale.y), Math.abs(_scale.z))
        if (r < MIN_RADIUS) o.castShadow = false
      })
    }
    sweep()
    const id = setInterval(sweep, EVERY_MS)
    return () => clearInterval(id)
  }, [scene])

  return null
}

export default ShadowPolicy
